import { useCallback, useEffect, useState } from "react";
import { useQueryParam, useQueryParams, encodeQueryParams, objectToSearchString } from "use-query-params";
import { isAbsoluteTimeRange, isRelativeTimeRange, isDurationString, DEFAULT_TIME_RANGE, isValidDate, useDeepMemo } from "../../../core";
import { throwDevError } from "../../../core/utils/logging";
export const TIME_RANGE_URL_PARAMS = {
    start: 'start',
    end: 'end'
};
/* Interprets an encoded string and returns either the string or null/undefined if not available */ function getEncodedValue(input, allowEmptyString) {
    if (input == null) {
        return input;
    }
    // '' or []
    if (input.length === 0 && (!allowEmptyString || allowEmptyString && input !== '')) {
        return null;
    }
    const str = input instanceof Array ? input[0] : input;
    if (str == null) {
        return str;
    }
    if (!allowEmptyString && str === '') {
        return null;
    }
    return str;
}
/* Encodes individual TimeRangeValue as a string, depends on whether start is relative or absolute */ export function encodeTimeRangeValue(timeOptionValue) {
    if (!timeOptionValue) {
        return timeOptionValue;
    }
    if (typeof timeOptionValue === 'string') {
        if (isDurationString(timeOptionValue)) {
            return timeOptionValue;
        }
    }
    return timeOptionValue.getTime().toString();
}
// Check for a JS timestamp, which should be 13 digits.
const TIMESTAMP_REGEX = /\d{13}/;
/* Converts param input to supported relative or absolute time range format */ export function decodeTimeRangeValue(input) {
    const paramString = getEncodedValue(input);
    if (paramString == null) return paramString;
    if (isDurationString(paramString)) {
        return paramString;
    }
    if (typeof paramString === 'string') {
        // Attempt to parse out timestamps because we have some edge cases in alerts where
        // the query string is erroneously appended with some extra content.
        const timestamp = paramString.match(TIMESTAMP_REGEX);
        if (!timestamp) {
            return undefined;
        }
        const timestampDate = new Date(Number(timestamp[0]));
        // Verify we have a real date.
        if (isValidDate(timestampDate)) {
            return timestampDate;
        }
    }
    return undefined;
}
/**
 * Custom TimeRangeValue param type
 * See: https://github.com/pbeshai/use-query-params/tree/master/packages/serialize-query-params#param-types
 */ export const TimeRangeParam = {
    encode: encodeTimeRangeValue,
    decode: decodeTimeRangeValue,
    equals: (valueA, valueB)=>{
        if (valueA === valueB) return true;
        if (valueA == null || valueB == null) return valueA === valueB;
        return valueA.valueOf() === valueB.valueOf();
    }
};
export const timeRangeQueryConfig = {
    start: TimeRangeParam,
    end: TimeRangeParam
};
export const refreshIntervalQueryConfig = {
    refresh: TimeRangeParam
};
/**
 * Gets the initial time range taking into account URL params and dashboard JSON duration
 * Sets start query param if it is empty on page load
 * @deprecated
 */ export function useInitialTimeRange(defaultTimeRange = DEFAULT_TIME_RANGE) {
    const [query] = useQueryParams(timeRangeQueryConfig, {
        updateType: 'replaceIn'
    });
    const { start, end } = query;
    // We use useDeepMemo here to prevent this code from re-running on every query param change,
    // ensuring it only triggers when the actual values change. While useDeepMemo can have
    // performance implications, it's suitable in this case since the dependency array is small
    // and doesn't include any complex objects.
    return useDeepMemo(()=>{
        let initialTimeRange = defaultTimeRange;
        if (!start) {
            return initialTimeRange;
        }
        const startStr = start.toString();
        if (isDurationString(startStr)) {
            initialTimeRange = {
                pastDuration: startStr
            };
        } else if (typeof start !== 'string' && isValidDate(start) && end && typeof end !== 'string' && isValidDate(end)) {
            initialTimeRange = {
                start: start,
                end: end
            };
        }
        return initialTimeRange;
    }, [
        start,
        end,
        defaultTimeRange
    ]);
}
/**
 * Returns time range getter and setter, set enabledURLParams to false to disable query string serialization
 */ export function useSetTimeRangeParams(initialTimeRange, enabledURLParams = true) {
    const [query, setQuery] = useQueryParams(timeRangeQueryConfig, {
        updateType: 'pushIn'
    });
    // determine whether initial param had previously been populated to fix back btn
    const [paramsLoaded, setParamsLoaded] = useState(false);
    // optional fallback when app does not want query string as source of truth
    // this occurs when enabledURLParams is set to false on TimeRangeProvider
    const [timeRangeState, setTimeRangeState] = useState(initialTimeRange);
    const { start, end } = query;
    useEffect(()=>{
        // when dashboard loaded with no params, default to dashboard duration
        // on the initial load, we want to use replaceIn to navigate back to the previous page.
        if (enabledURLParams && !paramsLoaded && !start) {
            if (isRelativeTimeRange(initialTimeRange)) {
                setQuery({
                    start: initialTimeRange.pastDuration,
                    end: undefined
                }, 'replaceIn');
                setParamsLoaded(true);
            }
            if (isAbsoluteTimeRange(initialTimeRange)) {
                setQuery(initialTimeRange, 'replaceIn');
                setParamsLoaded(true);
            }
        }
    }, [
        initialTimeRange,
        enabledURLParams,
        paramsLoaded,
        start,
        setQuery
    ]);
    const setTimeRangeQueryParams = useCallback((value, updateType)=>{
        if (isRelativeTimeRange(value)) {
            setQuery({
                start: value.pastDuration,
                end: undefined
            }, updateType);
        } else {
            if (!isValidDate(value.start) || !isValidDate(value.end)) {
                throwDevError('setTimeRangeQueryParams called with invalid dates');
                return;
            }
            setQuery({
                start: value.start,
                end: value.end
            }, updateType);
        }
    }, [
        setQuery
    ]);
    // We use useDeepMemo here to prevent this code from re-running on every query param change,
    // ensuring it only triggers when the actual values change. While useDeepMemo can have
    // performance implications, it's suitable in this case since the dependency array is small
    // and doesn't include any complex objects.
    return useDeepMemo(()=>{
        if (!enabledURLParams) {
            return {
                timeRange: timeRangeState,
                setTimeRange: setTimeRangeState
            };
        }
        let timeRange = initialTimeRange;
        // return initial time range if start time is not defined in the query params
        if (!start) {
            return {
                timeRange,
                setTimeRange: setTimeRangeQueryParams
            };
        }
        const startStr = start.toString();
        if (isDurationString(startStr)) {
            timeRange = {
                pastDuration: startStr
            };
        } else if (typeof start !== 'string' && isValidDate(start) && end && typeof end !== 'string' && isValidDate(end)) {
            timeRange = {
                start: start,
                end: end
            };
        }
        return {
            timeRange,
            setTimeRange: setTimeRangeQueryParams
        };
    }, [
        initialTimeRange,
        start,
        end,
        enabledURLParams,
        timeRangeState
    ]);
}
const DurationStringParam = {
    encode: (durationString)=>durationString,
    decode: (arrayStr)=>{
        if (typeof arrayStr === 'string') {
            return arrayStr;
        }
        return null;
    }
};
/**
 * Returns refresh interval getter and setter, set enabledURLParams to false to disable query string serialization
 */ export function useSetRefreshIntervalParams(args) {
    const { initialRefreshInterval = null, enabledURLParams } = args;
    const [refreshIntervalQuery, setRefreshIntervalQuery] = useQueryParam('refresh', DurationStringParam);
    const [refreshIntervalState, setRefreshIntervalState] = useState(initialRefreshInterval);
    if (!enabledURLParams) {
        return {
            refreshInterval: refreshIntervalState,
            setRefreshInterval: setRefreshIntervalState
        };
    }
    return {
        refreshInterval: refreshIntervalQuery,
        setRefreshInterval: setRefreshIntervalQuery
    };
}
/**
 * Convert a `TimeRangeValue` to a query string (without the `?`). Useful when generting links to pages that use time
 * range controls.
 */ export function timeRangeToQueryString(timeRangeValue) {
    if (isRelativeTimeRange(timeRangeValue)) {
        const encoded = encodeQueryParams(timeRangeQueryConfig, {
            start: timeRangeValue.pastDuration,
            end: timeRangeValue.end
        });
        return objectToSearchString(encoded);
    }
    const encoded = encodeQueryParams(timeRangeQueryConfig, {
        start: timeRangeValue.start,
        end: timeRangeValue.end
    });
    return objectToSearchString(encoded);
}

//# sourceMappingURL=query-params.js.map